/**
 * Screenshot Microservice for Shopify Product Customization
 * Captures screenshots AFTER cart add completes via event listener
 */

const ScreenshotService = {
  // State management
  isCapturing: false,
  deviceInfo: null,

  // Device detection and capabilities
  detectDevice: function() {
    if (this.deviceInfo) return this.deviceInfo;
    
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
                     (navigator.maxTouchPoints && navigator.maxTouchPoints > 2 && /MacIntel/.test(navigator.platform));
    
    const isTablet = /(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(navigator.userAgent);
    const isDesktop = !isMobile && !isTablet;
    
    // Detect performance capabilities
    const deviceMemory = navigator.deviceMemory || 4; // Default to 4GB if not available
    const hardwareConcurrency = navigator.hardwareConcurrency || 4;
    
    // Screen metrics
    const screenWidth = window.screen.width;
    const screenHeight = window.screen.height;
    const pixelRatio = window.devicePixelRatio || 1;
    
    // Performance classification
    let performanceClass = 'medium';
    if (deviceMemory >= 8 && hardwareConcurrency >= 8) {
      performanceClass = 'high';
    } else if (deviceMemory <= 2 || hardwareConcurrency <= 2) {
      performanceClass = 'low';
    }
    
    this.deviceInfo = {
      isMobile,
      isTablet,
      isDesktop,
      deviceMemory,
      hardwareConcurrency,
      screenWidth,
      screenHeight,
      pixelRatio,
      performanceClass,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight
      }
    };
    
    
    return this.deviceInfo;
  },

  // Get adaptive configuration based on device
  getAdaptiveConfig: function() {
    const device = this.detectDevice();
    
    // Base configurations by device type and performance
    const configs = {
      mobile: {
        low: { productScale: 1.5, logoScale: 1, captureDelay: 1200, maxRetries: 1 },
        medium: { productScale: 2, logoScale: 1.5, captureDelay: 1000, maxRetries: 2 },
        high: { productScale: 3, logoScale: 2, captureDelay: 800, maxRetries: 3 }
      },
      tablet: {
        low: { productScale: 2, logoScale: 1.5, captureDelay: 1000, maxRetries: 2 },
        medium: { productScale: 3, logoScale: 2, captureDelay: 900, maxRetries: 2 },
        high: { productScale: 4, logoScale: 2.5, captureDelay: 800, maxRetries: 3 }
      },
      desktop: {
        low: { productScale: 3, logoScale: 2, captureDelay: 900, maxRetries: 2 },
        medium: { productScale: 4, logoScale: 2.5, captureDelay: 800, maxRetries: 3 },
        high: { productScale: 6, logoScale: 3, captureDelay: 600, maxRetries: 3 }
      }
    };
    
    let deviceType = 'desktop';
    if (device.isMobile) deviceType = 'mobile';
    else if (device.isTablet) deviceType = 'tablet';
    
    const adaptiveConfig = configs[deviceType][device.performanceClass];
    
    
    return adaptiveConfig;
  },

  // Configuration
  config: {
    webhookUrl: 'https://connect.tee.digital/webhook/screenshots',
    logoSelectorInput: 'input[name="properties[selected_logo]"]:checked',
    debug: false,
    captureDelay: 800, // Wait 800ms for cart drawer animation
    captureOptions: {
      product: {
        useCORS: true,
        allowTaint: true,
        backgroundColor: null,
        scale: 6, // Will be overridden by adaptive config
        logging: false,
        silent: true,
        removeContainer: true,
        ignoreElements: function(element) {
          // Skip elements that might cause issues on mobile
          return element.classList.contains('flickity-viewport') ||
                 element.classList.contains('cart-drawer') ||
                 element.tagName === 'SCRIPT' ||
                 element.tagName === 'NOSCRIPT';
        }
      },
      logo: {
        useCORS: true,
        allowTaint: true,
        backgroundColor: null,
        scale: 3, // Will be overridden by adaptive config
        logging: false,
        silent: true,
        removeContainer: true
      }
    },
    
    // Memory management settings
    memoryManagement: {
      maxCanvasSize: 16777216, // 16MB canvas limit for mobile
      cleanupDelay: 100,
      enableGC: true
    }
  },

  // Memory management utilities
  memoryUtils: {
    cleanupCanvas: function(canvas) {
      if (canvas && canvas.getContext) {
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.clearRect(0, 0, canvas.width, canvas.height);
        }
        canvas.width = 0;
        canvas.height = 0;
      }
    },
    
    cleanupBlob: function(blob) {
      if (blob && blob.stream) {
        try {
          blob.stream().cancel();
        } catch (e) {
          // Ignore cleanup errors
        }
      }
    },
    
    revokeObjectURL: function(url) {
      if (url && url.startsWith('blob:')) {
        try {
          URL.revokeObjectURL(url);
        } catch (e) {
          // Ignore cleanup errors
        }
      }
    },
    
    forceGarbageCollection: function() {
      if (window.gc) {
        try {
          window.gc();
        } catch (e) {
          // GC not available
        }
      }
    },
    
    checkMemoryPressure: function() {
      if (navigator.deviceMemory && navigator.deviceMemory <= 2) {
        return 'high';
      }
      
      // Check if we're on a mobile device with limited memory
      const device = ScreenshotService.detectDevice();
      if (device.isMobile && device.performanceClass === 'low') {
        return 'medium';
      }
      
      return 'low';
    }
  },

  // Find the selected logo element
  getSelectedLogoElement: function() {
    const logoInput = document.querySelector(this.config.logoSelectorInput);
    
    if (!logoInput) {
      return null;
    }

    const logoId = logoInput.getAttribute('data-logo-id');

    const logoItem = logoInput.closest('.product-logo-selector__item');
    if (!logoItem) {
      return null;
    }

    const logoElement = logoItem.querySelector('.product-logo-selector__svg');
    

    return logoElement;
  },
  
  getSelectedImageWrapper: function() {
    const device = this.detectDevice();
    
    // Method 1: Look for is-selected class (Flickity default)
    let selectedCell = document.querySelector('.product__media.is-selected');
    
    // Method 2: Check aria-hidden attribute
    if (!selectedCell) {
      const allCells = document.querySelectorAll('.product__media.flickity-cell');
      selectedCell = Array.from(allCells).find(el => 
        el.getAttribute('aria-hidden') === 'false'
      );
    }
    
    // Method 2.5: Check for opacity = 1 (your specific setup)
    if (!selectedCell) {
      const allCells = document.querySelectorAll('.product__media');
      selectedCell = Array.from(allCells).find(el => {
        const computedStyle = window.getComputedStyle(el);
        return computedStyle.opacity === '1';
      });
    }
    
    // Method 3: Find the visible one in viewport (enhanced for mobile)
    if (!selectedCell) {
      const allCells = document.querySelectorAll('.product__media.flickity-cell');
      selectedCell = Array.from(allCells).find(el => {
        const rect = el.getBoundingClientRect();
        
        if (device.isMobile) {
          // On mobile, be more lenient with visibility detection
          const tolerance = 50; // 50px tolerance
          return rect.left >= -tolerance && rect.left < (window.innerWidth + tolerance);
        } else {
          return rect.left >= 0 && rect.left < window.innerWidth;
        }
      });
    }
    
    // Method 4: Mobile fallback - get first visible image if still not found
    if (!selectedCell && device.isMobile) {
      const allMedia = document.querySelectorAll('.product__media');
      selectedCell = Array.from(allMedia).find(el => {
        const rect = el.getBoundingClientRect();
        return rect.top >= 0 && rect.top < window.innerHeight && 
               rect.width > 0 && rect.height > 0;
      });
    }
    
    if (selectedCell) {
      const wrapper = selectedCell.querySelector('.product-image-wrapper');
      
      // Mobile optimization: ensure wrapper is actually visible
      if (wrapper && device.isMobile) {
        const wrapperRect = wrapper.getBoundingClientRect();
        if (wrapperRect.width === 0 || wrapperRect.height === 0) {
          return null;
        }
      }
      
      return wrapper;
    }
    
    return null;
  },

  // Enhanced element ignoring for html2canvas
  shouldIgnoreElement: function(element) {
    if (!element || !element.tagName) return false;
    
    try {
      // Ignore problematic custom elements by tag name
      const tagName = element.tagName.toLowerCase();
      if (tagName === 'radio-swatch' || 
          tagName === 'product-variant-selector' ||
          tagName === 'product-grid-item') {
        return true;
      }
      
      // Ignore elements with 'is' attribute (custom elements)
      if (element.hasAttribute && element.hasAttribute('is')) {
        const isValue = element.getAttribute('is');
        if (isValue === 'radio-swatch' || 
            isValue === 'product-variant-selector' ||
            isValue === 'product-grid-item') {
          return true;
        }
      }
      
      // Ignore elements with problematic classes
      if (element.classList && element.classList.length > 0) {
        const problematicClasses = [
          'product-form',
          'product__form',
          'radio-swatch', 
          'variant-selector',
          'swatch-element',
          'product-form__input--swatch',
          'product__form__swatch',
          'product-grid-item',
          'flickity-cell', // Often contains problematic elements
          'product__media-list' // Can contain variant elements
        ];
        
        for (const className of problematicClasses) {
          if (element.classList.contains(className)) {
            return true;
          }
        }
        
        // Check for any class containing 'swatch' (but not logo-related)
        for (const cls of element.classList) {
          if (cls.includes('swatch') && !cls.includes('logo')) {
            return true;
          }
          // Also ignore variant-related classes
          if (cls.includes('variant') || cls.includes('radio-')) {
            return true;
          }
        }
      }
      
      // Ignore elements with problematic data attributes
      if (element.hasAttribute) {
        const problematicAttrs = [
          'data-variant-selector',
          'data-radio-swatch',
          'data-swatch',
          'data-grid-item-variant',
          'data-product-form',
          'data-variant-id'
        ];
        
        for (const attr of problematicAttrs) {
          if (element.hasAttribute(attr)) {
            return true;
          }
        }
      }
      
      // Don't filter out the main product wrapper or image elements
      if (element.classList) {
        if (element.classList.contains('product-image-wrapper') || 
            element.classList.contains('product-media') ||
            element.classList.contains('logo-universal-v2') ||
            element.tagName.toLowerCase() === 'img') {
          return false;
        }
      }
      
      // Special check: if element contains problematic children, ignore the whole subtree
      if (element.querySelector) {
        try {
          const hasProblematicChildren = element.querySelector('radio-swatch, [is="radio-swatch"], product-variant-selector, [is="product-variant-selector"], input[type="radio"], input[type="checkbox"]');
          if (hasProblematicChildren) {
            return true;
          }
        } catch (queryError) {
          // If querySelector fails, ignore the element to be safe
          return true;
        }
      }
      
    } catch (error) {
      // If we can't safely check the element, ignore it to be safe
      return true;
    }
    
    return false;
  },

  // Capture element as PNG using html2canvas with memory management
  captureElement: async function(element) {
    if (!element) {
      throw new Error('No element provided to capture');
    }


    if (typeof html2canvas === 'undefined') {
      throw new Error('html2canvas library not loaded');
    }

    // Check memory pressure
    const memoryPressure = this.memoryUtils.checkMemoryPressure();

    // No need to hide elements - we'll use enhanced ignoreElements instead

    // Get adaptive configuration
    const adaptiveConfig = this.getAdaptiveConfig();
    
    // Enhanced capture options with comprehensive element filtering
    const captureOptions = {
      useCORS: true,
      allowTaint: true,
      backgroundColor: null,
      scale: adaptiveConfig.productScale,
      logging: false,
      silent: true,
      removeContainer: true,
      foreignObjectRendering: true,
      scrollX: 0,
      scrollY: 0,
      ignoreElements: (element) => {
        return this.shouldIgnoreElement(element);
      },
      onclone: function(clonedDoc) {
        try {
          // AGGRESSIVE cleanup for custom elements that cause null reference errors
          const aggressiveSelectors = [
            'radio-swatch', 
            'product-variant-selector',
            '[is="radio-swatch"]',
            '[is="product-variant-selector"]',
            'custom-radio-swatch',
            '.radio-swatch',
            '.product-variant-selector',
            '[data-variant-selector]',
            '[data-radio-swatch]',
            '.variant-selector',
            '.swatch-element',
            '.product-form__variant-selector',
            // Additional problematic elements from error logs
            '.product-form__input--swatch',
            '.product__form__swatch',
            '[class*="swatch"]'
          ];
          
          // Remove all custom elements first - BEFORE any cloning happens
          aggressiveSelectors.forEach(selector => {
            try {
              const elements = clonedDoc.querySelectorAll(selector);
              elements.forEach(el => {
                try {
                  // Completely disconnect custom element from DOM immediately
                  if (el.parentNode) {
                    const replacement = clonedDoc.createElement('div');
                    replacement.style.display = 'none'; // Hide the replacement
                    el.parentNode.replaceChild(replacement, el);
                  }
                } catch (removeError) {
                  try {
                    // Force removal if replacement fails
                    if (el.parentNode) {
                      el.parentNode.removeChild(el);
                    }
                  } catch(forceError) {
                    // Final attempt - set to empty
                    try {
                      el.innerHTML = '';
                      el.outerHTML = '<div style="display:none"></div>';
                    } catch(e) {
                      }
                  }
                }
              });
            } catch (selectorError) {
            }
          });
          
          // Remove any remaining scripts that might interfere
          const scripts = clonedDoc.querySelectorAll('script');
          scripts.forEach(script => {
            try {
              if (script.parentNode) script.parentNode.removeChild(script);
            } catch(e) {}
          });
          
          // Remove other problematic elements
          const problematicSelectors = [
            '.cart-drawer',
            '.mobile-nav',
            '.sticky-header',
            '.product-recommendations',
            '.recently-viewed'
          ];
          
          problematicSelectors.forEach(selector => {
            try {
              const elements = clonedDoc.querySelectorAll(selector);
              elements.forEach(el => {
                try {
                  if (el.parentNode) el.parentNode.removeChild(el);
                } catch(e) {}
              });
            } catch(e) {}
          });
        
          // Get device info for cleanup decisions
          const deviceInfo = ScreenshotService.detectDevice();
          
          // On high memory pressure or mobile, remove more elements
          if (memoryPressure === 'high' || deviceInfo.isMobile) {
            const heavySelectors = ['.flickity-viewport', '.product__media:not(.is-selected)'];
            heavySelectors.forEach(selector => {
              try {
                const elements = clonedDoc.querySelectorAll(selector);
                elements.forEach(el => {
                  try {
                    if (el.parentNode) el.parentNode.removeChild(el);
                  } catch(e) {}
                });
              } catch(e) {}
            });
          }
          
          // Mobile-specific cleanup
          if (deviceInfo.isMobile) {
            const mobileProblematicSelectors = [
              '.product__media-toggle',
              '.product__media-list',
              '.flickity-prev-next-button',
              '.product-zoom',
              '[data-zoom]'
            ];
            
            mobileProblematicSelectors.forEach(selector => {
              try {
                const elements = clonedDoc.querySelectorAll(selector);
                elements.forEach(el => {
                  try {
                    if (el.parentNode) el.parentNode.removeChild(el);
                  } catch(e) {}
                });
              } catch(e) {}
            });
            
            // Force dimensions on mobile for better capture
            try {
              const mediaElements = clonedDoc.querySelectorAll('.product__media img');
              mediaElements.forEach(img => {
                try {
                  if (!img.style.width && !img.style.height) {
                    img.style.maxWidth = '100%';
                    img.style.height = 'auto';
                  }
                } catch(e) {}
              });
            } catch(e) {}
          }
          
        } catch (cloneError) {
          // Continue anyway, html2canvas might still work
        }
      }
    }; // Close captureOptions object

    let canvas = null;
    
    try {
      // Additional safety: Check if element still exists and is valid
      if (!element.isConnected || !document.contains(element)) {
        throw new Error('Element is no longer connected to document');
      }
      
      // Verify element has dimensions
      const rect = element.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) {
        throw new Error(`Element has zero dimensions: ${rect.width}x${rect.height}`);
      }
      
      
      // Use native Canvas API for non-blocking image composition
      
      try {
        // Extract product image and logo overlay data
        const productImage = element.querySelector('img');
        const logoOverlay = element.querySelector('.logo-universal-v2');
        
        if (!productImage) {
          throw new Error('No product image found');
        }
        
        // Create canvas for composition
        canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const scale = Math.min(adaptiveConfig.productScale, 2);
        
        // Set canvas dimensions
        canvas.width = rect.width * scale;
        canvas.height = rect.height * scale;
        
        // Load and draw product image
        const loadImage = (src) => new Promise((resolve, reject) => {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.onload = () => resolve(img);
          img.onerror = reject;
          img.src = src;
        });
        
        // Draw product image
        const productImg = await loadImage(productImage.src);
        ctx.drawImage(productImg, 0, 0, canvas.width, canvas.height);
        
        // Draw logo overlay if present
        if (logoOverlay) {
          const logoSvg = logoOverlay.querySelector('svg');
          
          if (logoSvg) {
            // Get logo position and size from inline styles (more reliable than getBoundingClientRect)
            const logoStyle = logoOverlay.style;
            const elementRect = element.getBoundingClientRect();
            
            // Parse position from inline styles
            const left = parseFloat(logoStyle.left) || 0;
            const top = parseFloat(logoStyle.top) || 0;
            const width = parseFloat(logoStyle.width) || 50; // Default fallback
            const height = parseFloat(logoStyle.height) || 50; // Default fallback
            
            // Calculate relative position (logo styles are already relative to the product wrapper)
            const relX = left * scale;
            const relY = top * scale;
            const logoWidth = width * scale;
            const logoHeight = height * scale;
            
            // Convert SVG to image and draw
            const svgData = new XMLSerializer().serializeToString(logoSvg);
            
            const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
            const svgUrl = URL.createObjectURL(svgBlob);
            
            try {
              const logoImg = await loadImage(svgUrl);
              ctx.drawImage(logoImg, relX, relY, logoWidth, logoHeight);
              URL.revokeObjectURL(svgUrl); // Cleanup
            } catch (logoError) {
              // Logo overlay failed, continue without logo
            }
          }
        }
        
      } catch (error) {
        // Native Canvas approach failed, falling back to basic html2canvas
        // Fallback to very basic html2canvas
        canvas = await html2canvas(element, {
          useCORS: true,
          allowTaint: true,
          scale: 1,
          logging: false,
          silent: true,
          ignoreElements: (el) => {
            const tag = el.tagName?.toLowerCase() || '';
            return tag.includes('-') || ['script', 'style', 'input', 'button'].includes(tag);
          }
        });
      }
      
      // Check canvas size for memory limits
      const canvasSize = canvas.width * canvas.height * 4; // 4 bytes per pixel
      if (canvasSize > this.config.memoryManagement.maxCanvasSize) {
        // Scale down canvas if too large
        const ratio = Math.sqrt(this.config.memoryManagement.maxCanvasSize / canvasSize);
        const newCanvas = document.createElement('canvas');
        newCanvas.width = Math.floor(canvas.width * ratio);
        newCanvas.height = Math.floor(canvas.height * ratio);
        
        const ctx = newCanvas.getContext('2d');
        ctx.drawImage(canvas, 0, 0, newCanvas.width, newCanvas.height);
        
        // Clean up original canvas
        this.memoryUtils.cleanupCanvas(canvas);
        canvas = newCanvas;
      }
      
      return new Promise((resolve, reject) => {
        const cleanup = () => {
          setTimeout(() => {
            if (canvas) {
              this.memoryUtils.cleanupCanvas(canvas);
              canvas = null;
            }
            
            if (this.config.memoryManagement.enableGC) {
              this.memoryUtils.forceGarbageCollection();
            }
          }, this.config.memoryManagement.cleanupDelay);
        };
        
        canvas.toBlob((blob) => {
          if (blob) {
            cleanup();
            resolve(blob);
          } else {
            cleanup();
            reject(new Error('Failed to create blob from canvas'));
          }
        }, 'image/png', memoryPressure === 'high' ? 0.8 : 0.95); // Lower quality on high memory pressure
      });
    } catch (error) {
      if (canvas) {
        this.memoryUtils.cleanupCanvas(canvas);
      }
      
      
      // Check if it's a cloning/custom element error
      if (error.message && (error.message.includes('Unable to find element') || 
                           error.message.includes('cloned iframe') ||
                           error.message.includes('getAttribute') ||
                           error.message.includes('RadioSwatch'))) {
        
        
        // Don't throw error for custom element issues - let the process continue
        // This allows logo capture to still work even if product capture fails
        return null;
      }
      
      throw error;
    }
  },

  // Alternative product capture method avoiding html2canvas custom element issues
  captureProductImage: async function(productWrapper) {
    if (!productWrapper) {
      throw new Error('No product wrapper provided');
    }


    // Check if wrapper contains a logo overlay from Logo System Universal
    const logoOverlay = productWrapper.querySelector('.logo-universal-v2');
    
    if (logoOverlay) {
      // Use html2canvas to capture the entire wrapper (product + logo)
      return await this.captureElement(productWrapper);
    }

    // Find the actual product image
    const productImg = productWrapper.querySelector('img');
    if (!productImg) {
      throw new Error('No product image found in wrapper');
    }

    // Get adaptive configuration
    const adaptiveConfig = this.getAdaptiveConfig();
    const memoryPressure = this.memoryUtils.checkMemoryPressure();
    const device = this.detectDevice();
    
    // Wait for image to load if not already loaded
    if (!productImg.complete) {
      await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => reject(new Error('Image load timeout')), 5000);
        productImg.onload = () => {
          clearTimeout(timeout);
          resolve();
        };
        productImg.onerror = () => {
          clearTimeout(timeout);
          reject(new Error('Image load failed'));
        };
      });
    }

    // Get image dimensions and calculate canvas size
    const imgRect = productImg.getBoundingClientRect();
    const naturalWidth = productImg.naturalWidth || imgRect.width;
    const naturalHeight = productImg.naturalHeight || imgRect.height;
    
    // Apply adaptive scaling
    const scale = adaptiveConfig.productScale;
    let canvasWidth = Math.floor(naturalWidth * scale / 6); // Normalize scale
    let canvasHeight = Math.floor(naturalHeight * scale / 6);
    
    // Apply memory pressure limits
    if (memoryPressure === 'high') {
      const maxDimension = device.isMobile ? 800 : 1200;
      if (canvasWidth > maxDimension || canvasHeight > maxDimension) {
        const ratio = Math.min(maxDimension / canvasWidth, maxDimension / canvasHeight);
        canvasWidth = Math.floor(canvasWidth * ratio);
        canvasHeight = Math.floor(canvasHeight * ratio);
      }
    }

    let canvas = null;
    
    return new Promise((resolve, reject) => {
      try {
        // Create canvas
        canvas = document.createElement('canvas');
        canvas.width = canvasWidth;
        canvas.height = canvasHeight;
        
        const ctx = canvas.getContext('2d');
        
        // Enable image smoothing for better quality
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        
        // Handle CORS for external images
        const tempImg = new Image();
        tempImg.crossOrigin = 'anonymous';
        
        const cleanup = () => {
          setTimeout(() => {
            if (canvas) {
              this.memoryUtils.cleanupCanvas(canvas);
              canvas = null;
            }
          }, this.config.memoryManagement.cleanupDelay);
        };
        
        tempImg.onload = () => {
          try {
            // Draw image to canvas
            ctx.drawImage(tempImg, 0, 0, canvasWidth, canvasHeight);
            
            // Convert to blob
            const quality = memoryPressure === 'high' ? 0.8 : 0.95;
            canvas.toBlob(
              blob => {
                cleanup();
                if (blob) {
                  resolve(blob);
                } else {
                  reject(new Error('Failed to create blob from product canvas'));
                }
              },
              'image/png',
              quality
            );
          } catch (error) {
            cleanup();
            reject(error);
          }
        };
        
        tempImg.onerror = () => {
          cleanup();
          reject(new Error('Failed to load product image'));
        };
        
        // Use the original image source
        tempImg.src = productImg.src;
        
      } catch (error) {
        if (canvas) this.memoryUtils.cleanupCanvas(canvas);
        reject(error);
      }
    });
  },

  // Capture logo SVG with memory management and adaptive sizing
  captureLogoSvg: async function(logoElement) {
    if (!logoElement) {
      throw new Error('No logo element provided');
    }


    // Find the actual SVG element inside the container
    const svgElement = logoElement.querySelector('svg');
    
    if (!svgElement) {
      throw new Error('No SVG found inside logo element');
    }

    // Get adaptive configuration
    const adaptiveConfig = this.getAdaptiveConfig();
    const memoryPressure = this.memoryUtils.checkMemoryPressure();
    
    // Adaptive dimensions based on device and memory pressure
    let dimensions = 500; // Default
    if (memoryPressure === 'high') {
      dimensions = 300;
    } else if (memoryPressure === 'medium') {
      dimensions = 400;
    }
    
    // Scale based on adaptive config
    dimensions = Math.floor(dimensions * adaptiveConfig.logoScale / 3); // Normalize scale

    let svgBlob = null;
    let objectURL = null;
    let canvas = null;

    return new Promise((resolve, reject) => {
      try {
        // Serialize SVG to string
        const serializer = new XMLSerializer();
        let svgString = serializer.serializeToString(svgElement);

        // Extract texture with mix-blend-mode for separate compositing
        // CSS mix-blend-mode doesn't render reliably when SVG is drawn to canvas
        // Try both attribute orders (style before href, or href before style)
        const textureMatch = svgString.match(/<image[^>]*style="[^"]*mix-blend-mode:\s*multiply[^"]*"[^>]*href="([^"]+)"[^>]*\/?>/i)
                          || svgString.match(/<image[^>]*href="([^"]+)"[^>]*style="[^"]*mix-blend-mode:\s*multiply[^"]*"[^>]*\/?>/i);
        let textureDataUri = null;

        if (textureMatch) {
          textureDataUri = textureMatch[1];
          // Remove texture from SVG for clean base render
          svgString = svgString.replace(/<image[^>]*mix-blend-mode[^>]*\/?>/gi, '');
        }

        if (!svgString.includes('width=') && !svgString.includes('height=')) {
          svgString = svgString.replace(
            '<svg',
            `<svg width="${dimensions}" height="${dimensions}"`
          );
        }

        // Create blob URL from SVG
        svgBlob = new Blob([svgString], { 
          type: 'image/svg+xml;charset=utf-8' 
        });
        objectURL = URL.createObjectURL(svgBlob);
        
        // Load SVG as image
        const img = new Image();
        
        const cleanup = () => {
          if (objectURL) {
            this.memoryUtils.revokeObjectURL(objectURL);
            objectURL = null;
          }
          if (svgBlob) {
            this.memoryUtils.cleanupBlob(svgBlob);
            svgBlob = null;
          }
          if (canvas) {
            setTimeout(() => {
              this.memoryUtils.cleanupCanvas(canvas);
              canvas = null;
            }, this.config.memoryManagement.cleanupDelay);
          }
        };
        
        img.onload = () => {
          try {
            // Create canvas
            canvas = document.createElement('canvas');
            canvas.width = dimensions;
            canvas.height = dimensions;

            const ctx = canvas.getContext('2d');

            // White background
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, dimensions, dimensions);

            // Draw base SVG (without texture if it was extracted)
            ctx.drawImage(img, 0, 0, dimensions, dimensions);

            // Function to finalize and convert canvas to blob
            const finalizeCanvas = () => {
              const quality = memoryPressure === 'high' ? 0.7 : 0.95;
              canvas.toBlob(
                blob => {
                  cleanup();
                  if (blob) {
                    resolve(blob);
                  } else {
                    reject(new Error('Failed to create blob from SVG'));
                  }
                },
                'image/png',
                quality
              );
            };

            // Apply texture with multiply blend if present
            // Canvas globalCompositeOperation is reliable across all browsers
            // unlike CSS mix-blend-mode in SVG which renders inconsistently
            if (textureDataUri) {
              const textureImg = new Image();
              textureImg.onload = () => {
                ctx.globalCompositeOperation = 'multiply';
                ctx.drawImage(textureImg, 0, 0, dimensions, dimensions);
                ctx.globalCompositeOperation = 'source-over';
                finalizeCanvas();
              };
              textureImg.onerror = () => {
                // If texture fails to load, still output the base logo
                console.warn('[Screenshot] Texture failed to load, using base logo only');
                finalizeCanvas();
              };
              textureImg.src = textureDataUri;
            } else {
              // No texture, finalize immediately
              finalizeCanvas();
            }
          } catch (error) {
            cleanup();
            reject(error);
          }
        };
        
        img.onerror = () => {
          cleanup();
          reject(new Error('Failed to load SVG as image'));
        };
        
        img.src = objectURL;
      } catch (error) {
        if (objectURL) this.memoryUtils.revokeObjectURL(objectURL);
        if (svgBlob) this.memoryUtils.cleanupBlob(svgBlob);
        reject(error);
      }
    });
  },

  // Send PNGs to webhook endpoint with retry logic and error handling
  sendToWebhook: async function(screenshots, metadata = {}) {

    const device = this.detectDevice();
    const adaptiveConfig = this.getAdaptiveConfig();
    const maxRetries = adaptiveConfig.maxRetries || 2;
    
    const formData = new FormData();
    
    if (screenshots.product) {
      formData.append('productImage', screenshots.product, 'product-customization.png');
    }
    
    if (screenshots.logo) {
      formData.append('logoImage', screenshots.logo, 'logo-selection.png');
    }
    
    // Add device info to metadata
    const enrichedMetadata = {
      ...metadata,
      deviceInfo: {
        type: device.isMobile ? 'mobile' : (device.isTablet ? 'tablet' : 'desktop'),
        performanceClass: device.performanceClass,
        viewport: device.viewport,
        userAgent: navigator.userAgent
      },
      captureSettings: {
        productScale: adaptiveConfig.productScale,
        logoScale: adaptiveConfig.logoScale,
        captureDelay: adaptiveConfig.captureDelay
      }
    };
    
    formData.append('metadata', JSON.stringify(enrichedMetadata));

    let lastError = null;
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        
        // Add timeout for mobile devices
        const timeoutMs = device.isMobile ? 30000 : 15000; // 30s for mobile, 15s for desktop
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
        
        const response = await fetch(this.config.webhookUrl, {
          method: 'POST',
          body: formData,
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);

        if (!response.ok) {
          throw new Error(`Webhook returned ${response.status}: ${response.statusText}`);
        }

        const result = await response.json();
        

        return result;
        
      } catch (error) {
        lastError = error;
        
        
        // Don't retry on certain errors
        if (error.name === 'AbortError') {
          // Request timed out
        } else if (error.message.includes('400') || error.message.includes('401') || error.message.includes('403')) {
          // Don't retry on client errors
          break;
        }
        
        // Wait before retry (exponential backoff)
        if (attempt < maxRetries) {
          const delay = Math.min(1000 * Math.pow(2, attempt - 1), 5000); // Cap at 5s
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }
    
    
    // On mobile, try to store for later retry (basic implementation)
    if (device.isMobile && 'serviceWorker' in navigator) {
      try {
        // Could implement offline storage here
      } catch (e) {
        // Ignore storage errors
      }
    }
    
    throw lastError;
  },

  // Gather metadata about the product and customization
  gatherMetadata: function(lineItemId = null, selectedLogoId = null) {
    const metadata = {
      timestamp: new Date().toISOString(),
      url: window.location.href
    };

    if (lineItemId) {
      metadata.lineItemId = lineItemId;
    }

    if (selectedLogoId) {
      metadata.selectedLogoId = selectedLogoId;
    }

    try {
      const productJson = document.querySelector('[data-product-json]');
      if (productJson) {
        const product = JSON.parse(productJson.textContent);
        metadata.productId = product.id;
        metadata.productTitle = product.title;
        metadata.productHandle = product.handle;
      }

      const variantInput = document.querySelector('[name="id"]');
      if (variantInput) {
        metadata.variantId = variantInput.value;
      }

      const quantityInput = document.querySelector('[name="quantity"]');
      if (quantityInput) {
        metadata.quantity = quantityInput.value;
      }

      if (window.Shopify && window.Shopify.cartToken) {
        metadata.cartToken = window.Shopify.cartToken;
      }

      if (window.Shopify && window.Shopify.customer) {
        metadata.customerId = window.Shopify.customer.id;
      }

    } catch (e) {
    }

    return metadata;
  },

  // Process screenshots after cart add with performance optimizations
  processScreenshots: async function(cartResponse) {
    if (this.isCapturing) {
      return;
    }

    this.isCapturing = true;
    
    // Use setTimeout to avoid blocking the UI thread immediately
    setTimeout(async () => {
      await this.performScreenshotCapture(cartResponse);
    }, 100); // Small delay to let UI update
  },

  // Actual screenshot capture logic (separated to avoid blocking)
  performScreenshotCapture: async function(cartResponse) {
    try {
      // Initialize device detection and get adaptive config
      const device = this.detectDevice();
      const adaptiveConfig = this.getAdaptiveConfig();
      
      // Performance-based capture delay
      const captureDelay = adaptiveConfig.captureDelay;

      // Wait for cart drawer animation and UI updates (adaptive timing)
      await new Promise(resolve => setTimeout(resolve, captureDelay));
      
      // Additional wait for mobile devices with slower rendering
      if (device.isMobile && device.performanceClass === 'low') {
        await new Promise(resolve => setTimeout(resolve, 200));
      }
      
      // Mobile-specific: Wait for any pending image loads
      if (device.isMobile) {
        const images = document.querySelectorAll('.product__media img');
        const imageLoadPromises = Array.from(images).map(img => {
          if (img.complete) return Promise.resolve();
          
          return new Promise(resolve => {
            const timeout = setTimeout(resolve, 1000); // Don't wait more than 1s
            img.onload = () => {
              clearTimeout(timeout);
              resolve();
            };
            img.onerror = () => {
              clearTimeout(timeout);
              resolve();
            };
          });
        });
        
        if (imageLoadPromises.length > 0) {
          await Promise.all(imageLoadPromises);
        }
      }

      
      // Fetch the full cart to get cart token
      let cartToken = null;
      
      try {
        const fullCartResponse = await fetch('/cart.js');
        const cart = await fullCartResponse.json();
        
        // Cart token format: "hWN0i2mB4U3fDXx71TDTl1xy?key=6fc89937f144a28bfb7f16712f98a0f3"
        // Extract just the first part before ?key=
        cartToken = cart.token ? cart.token.split('?')[0] : cart.token;
        
      } catch (err) {
      }
      
      // Capture product image (always includes logo overlay if present)
      const productWrapper = this.getSelectedImageWrapper();
      let productBlob = null;
      
      
      if (productWrapper) {
        try {
          // Find the specific wrapper that contains both image and logo
          const imageWrapper = productWrapper.querySelector('.product-image-wrapper') || 
                              productWrapper.querySelector('[class*="image-wrapper"]') ||
                              productWrapper;
          
          
          // Ensure element is visible and stable before capture
          if (imageWrapper.offsetWidth === 0 || imageWrapper.offsetHeight === 0) {
            throw new Error('Element has no dimensions');
          }
          
          // Always use html2canvas to capture complete composition (product + any logo overlay)
          productBlob = await this.captureElement(imageWrapper);
        } catch (captureError) {
          
          // Fallback: Try capturing a broader element
          if (captureError?.message?.includes('Unable to find element')) {
            try {
              const fallbackElement = productWrapper.closest('.product__media-gallery') || 
                                    productWrapper.closest('.product-media') ||
                                    productWrapper.parentElement;
              
              if (fallbackElement) {
                productBlob = await this.captureElement(fallbackElement);
              }
            } catch (fallbackError) {
              // Fallback capture also failed
            }
          }
        }
      }
      
      // Capture standalone logo (separate from product capture)
      const logoElement = this.getSelectedLogoElement();
      let logoBlob = null;
      
      if (logoElement) {
        try {
          logoBlob = await this.captureLogoSvg(logoElement);
        } catch (err) {
        }
      }
      
      // Get selected logo ID
      const logoInput = document.querySelector(this.config.logoSelectorInput);
      const selectedLogoId = logoInput ? logoInput.getAttribute('data-logo-id') : null;
      
      // Gather metadata
      const metadata = this.gatherMetadata(cartResponse.key, selectedLogoId);
      
      // Add cart token
      if (cartToken) {
        metadata.cartToken = cartToken;
      }
      
      // Add cart response data
      if (cartResponse.key) {
        metadata.lineItemKey = cartResponse.key;
      }
      
      // Extract URLs from properties - these ARE the mockup URLs where screenshots should be saved
      if (cartResponse.properties) {
        if (cartResponse.properties._product_image_url) {
          metadata.productImageUrl = cartResponse.properties._product_image_url;
          // Extract just the path after productMockups/ and remove ?key= parameter
          let cleanUrl = cartResponse.properties._product_image_url
            .replace('https://assets.tee.digital/productMockups/', '')
            .replace(/\?key=[^_]+/, ''); // Remove ?key=xxx from the middle
          metadata.screenshotProductUrl = cleanUrl;
        }
        if (cartResponse.properties._logo_url) {
          metadata.logoUrl = cartResponse.properties._logo_url;
          // Extract just the path after logoMockups/ and remove ?key= parameter
          let cleanUrl = cartResponse.properties._logo_url
            .replace('https://assets.tee.digital/logoMockups/', '')
            .replace(/\?key=[^_]+/, ''); // Remove ?key=xxx from the middle
          metadata.screenshotLogoUrl = cleanUrl;
        }
        if (cartResponse.properties._storefront_code) {
          metadata.storefrontCode = cartResponse.properties._storefront_code;
        }
      }
      
      metadata.lineItemVariantId = cartResponse.variant_id;
      metadata.lineItemProductId = cartResponse.product_id;
      metadata.lineItemQuantity = cartResponse.quantity;
      metadata.lineItemPrice = cartResponse.price;
      
      
      // Send screenshots to webhook (send even if only logo captured)
      if (productBlob || logoBlob) {
        const screenshots = {
          product: productBlob,
          logo: logoBlob
        };
        
        try {
          await this.sendToWebhook(screenshots, metadata);
        } catch (webhookError) {
          // Don't throw here - we've done what we can
        }
      } else {
        
        // Still send metadata to webhook for tracking purposes
        try {
          await this.sendToWebhook({ product: null, logo: null }, {
            ...metadata,
            captureStatus: 'failed',
            reason: 'No screenshots captured'
          });
        } catch (e) {
          // Ignore metadata-only webhook failures
        }
      }
      
    } catch (error) {
    } finally {
      // Reset capturing flag after adaptive delay
      const deviceInfo = this.detectDevice();
      const resetDelay = deviceInfo.isMobile ? 1500 : 1000;
      setTimeout(() => {
        this.isCapturing = false;
        
        // Additional cleanup for mobile
        if (deviceInfo.isMobile && this.config.memoryManagement.enableGC) {
          setTimeout(() => {
            this.memoryUtils.forceGarbageCollection();
          }, 500);
        }
      }, resetDelay);
    }
  },

  // Setup event listeners with mobile optimizations
  setupEventListener: function() {
    const self = this;
    
    // Detect device capabilities
    const device = this.detectDevice();
    
    // Listen for custom cart add events from Quick Add
    document.addEventListener('cartItemAdded', (e) => {
      
      if (e.detail) {
        // On low-performance devices, add slight delay to prevent blocking UI
        if (device.performanceClass === 'low') {
          setTimeout(() => {
            self.processScreenshots(e.detail);
          }, 100);
        } else {
          self.processScreenshots(e.detail);
        }
      }
    });
    
    // Mobile-specific optimizations
    if (device.isMobile) {
      // Handle orientation changes
      window.addEventListener('orientationchange', () => {
        // Reset device info on orientation change
        this.deviceInfo = null;
        
      });
      
      // Handle visibility changes (mobile browsers often pause when not visible)
      document.addEventListener('visibilitychange', () => {
      });
      
      // Memory warning handling for Safari iOS
      window.addEventListener('pagehide', () => {
        // Force cleanup on page hide
        this.memoryUtils.forceGarbageCollection();
      });
    }

  },

  // Initialize the service with device detection
  init: function() {
    
    // Detect device and log capabilities
    const device = this.detectDevice();
    const adaptiveConfig = this.getAdaptiveConfig();
    
    
    // Update capture delay based on device
    this.config.captureDelay = adaptiveConfig.captureDelay;

    // Listen for cart add events
    this.setupEventListener();
    
    // Pre-warm html2canvas on high-performance devices
  }
};

// Auto-initialize when script loads
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    ScreenshotService.init();
  });
} else {
  ScreenshotService.init();
}

// Expose to window for manual testing
window.ScreenshotService = ScreenshotService;